// Relationship Progression Layer (RPL) — Phase 1, SHADOW MODE, frontend-only.
//
// A purely DESCRIPTIVE, deterministic measurement of relational disclosure depth.
// It silently observes a conversation and reports how relationally deep the thread
// is (a sticky 0-4 ladder) and whether that depth is advancing. It NEVER touches
// reply generation, prompts, Best Move, Signal Check, Destination Progress,
// Closing Readiness, navigation, or any UI. Awareness, not control.
//
// Computed entirely on the frontend from the conversation text already available
// (reuses the conversationState parser + engagement reconstruction). Its entire
// value in Phase 1 is producing labeled data to pair with Outcome Feedback, so we
// can later answer: "Do conversations that advance relationally perform better?"

import { parseConversationTurns, reconstructStateFromHistory } from "./conversationState";

// Sticky 0-4 disclosure-depth ladder (orthogonal to engagement / flirtation / logistics).
//   0 Surface              — banter, jokes, low-risk reactions, profile/bio chat
//   1 Opinions             — real takes / preferences
//   2 Stories              — specific lived experiences
//   3 Emotional/Investment — feelings, what matters, goals, fears
//   4 Vulnerability/Trust  — genuine admissions, trust signals
export const RPL_STAGE_LABELS = [
  "Surface",
  "Opinions",
  "Stories",
  "Emotional / Investment",
  "Vulnerability / Trust",
];

// Short audit evidence per rung (NOT user-facing — logging / QA only).
const STAGE_EVIDENCE = {
  0: "Surface chat",
  1: "Shared an opinion",
  2: "Shared a story",
  3: "Emotional disclosure",
  4: "Vulnerability / trust signal",
};

// Stagnation window: turns since the last new rung while still engaged.
export const RPL_STAGNATION_K = 4;

// --- Content markers (checked deepest-first; first match wins) ---
const VULNERABILITY_RE = /\b(i (don'?t|do not) usually (tell|say|share|open up|do this)|never (told|admitted|said this to) (anyone|anybody|this)|i'?ve never told|hard for me to (say|admit|talk about)|i don'?t (really )?(tell|talk about) (people|this|anyone)|i (really )?trust you|between us|my (biggest )?(insecurit|fear)\w*|i struggle with|i'?m scared to (say|admit)|i don'?t open up( to people)?|this is (really )?personal)\b/i;

const EMOTIONAL_RE = /\b(means? (a lot|so much|the world)|i care (about|deeply)|family means|matters? (a lot |so much |deeply )?to me|i'?ve been feeling|i feel (lonely|scared|anxious|nervous|sad|down|stuck|lost|overwhelmed|empty|hopeful)|i'?m (scared|anxious|nervous|lonely|stressed|depressed|burned out|overwhelmed|heartbroken)|my (goal|dream|biggest goal)\w*|i (really )?want to (be|become|build|find|figure out)|passionate about|you'?re (so |really )?easy to talk to|i (really )?enjoy talking to you|i value\b|grief|grieving|passed away|miss (her|him|them|my)|my (mom|dad|parents|family))\b/i;

const STORY_RE = /\b(this one time|one time|last (night|week|year|month|time)|i (once|went|got|saw|did|tried|remember|used to|ended up|grew up)|reminds me of (when|the time)|back when|growing up|when i was|so basically|the other day|(a )?few years ago|in college|on my trip)\b/i;

const OPINION_RE = /\b(i think|i feel like|honestly|actually|imo|tbh|i'?d say|in my opinion|i believe|i prefer|i love|i hate|can'?t stand|pet peeve|my favou?rite|i'?m a (big )?fan|not a fan|overrated|underrated|unpopular opinion)\b/i;

// Classify a single message into a disclosure rung (0-4).
export const classifyTurnStage = (text) => {
  const t = (text || "").toLowerCase();
  if (!t.trim()) return 0;
  if (VULNERABILITY_RE.test(t)) return 4;
  if (EMOTIONAL_RE.test(t)) return 3;
  if (STORY_RE.test(t)) return 2;
  if (OPINION_RE.test(t)) return 1;
  return 0;
};

// Compute the shadow RPL snapshot from a conversation history.
// Returns null when there is no usable history (caller stores nothing).
// options.closingReadiness gates false stagnation on threads that are closing.
export const computeRelationshipStage = (history, contactName, options = {}) => {
  const turns = parseConversationTurns(history, contactName).filter((t) => t.text);
  if (!turns.length) return null;

  const perTurnStages = turns.map((t) => classifyTurnStage(t.text));

  // Running max ratchets (you can't un-know a disclosure).
  let highest = 0;
  let lastAdvanceIdx = -1;
  perTurnStages.forEach((s, i) => {
    if (s > highest) {
      highest = s;
      lastAdvanceIdx = i;
    }
  });

  // Current-turn depth = deepest of the last two turns (can dip below the max).
  const recent = perTurnStages.slice(-2);
  const currentTurnStage = recent.length ? Math.max(...recent) : 0;

  // Turns elapsed since the last NEW rung landed.
  const turnsSinceLastAdvancement =
    lastAdvanceIdx === -1 ? turns.length : turns.length - 1 - lastAdvanceIdx;

  // Engagement contrast signal (reuse the existing reconstruction).
  const recon = reconstructStateFromHistory(history, contactName);
  const engagementActive = recon.reciprocityCount >= 2 || recon.avgReplyLength >= 5;

  // Closing threads must never be flagged as stagnating (spec safeguard).
  const closing = options.closingReadiness;
  const isClosing = closing === "READY" || closing === "MAYBE_READY";

  let progressionState;
  if (highest > 0 && lastAdvanceIdx >= turns.length - 2) {
    progressionState = "advancing"; // a new rung landed within the last 2 turns
  } else if (currentTurnStage < highest - 1) {
    progressionState = "regressing"; // retreated well below the achieved depth
  } else if (turnsSinceLastAdvancement >= RPL_STAGNATION_K && engagementActive && !isClosing) {
    progressionState = "stagnating"; // engaged but not deepening
  } else {
    progressionState = "holding";
  }

  return {
    relationship_stage: highest, // sticky running max (the headline primitive)
    highest_stage: highest, // alias for clarity
    current_turn_stage: currentTurnStage,
    turns_since_last_advancement: turnsSinceLastAdvancement,
    progression_state: progressionState,
    stage_evidence: STAGE_EVIDENCE[highest],
    per_turn_stages: perTurnStages,
  };
};
